import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, Check, X, Sparkles, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RowActions } from "@/components/dashboard/RowActions";
import usePagination from "@/lib/usePagination";
import TablePagination from "@/components/dashboard/TablePagination";

const EMPTY = { title: "", location: "", department: "", industry: "Information Technology", experience: "0-2 years", description: "", skills: "" };

const STATUS_STYLE = {
  approved: "border-emerald-500 text-emerald-600",
  pending:  "border-amber-500 text-amber-600",
  rejected: "border-rose-500 text-rose-600",
};

export default function Jobs() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isClient = user?.role === "client";
  const [jobs, setJobs] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [clientMap, setClientMap] = useState({});
  const [pendingOnly, setPendingOnly] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [drafting, setDrafting] = useState(false);

  const draftWithAI = async () => {
    if (!f.title || f.title.trim().length < 3) {
      toast.error("Enter a role title first — the AI needs it to draft the JD.");
      return;
    }
    setDrafting(true);
    try {
      const skills = (f.skills || "").split(",").map((s) => s.trim()).filter(Boolean);
      const { data } = await api.post("/util/draft-job", {
        title: f.title, industry: f.industry, experience: f.experience, skills,
      });
      // Compose a single JD body combining description + responsibilities + requirements.
      const body = [
        data.description || "",
        data.responsibilities ? `\n\nResponsibilities:\n${data.responsibilities}` : "",
        data.requirements ? `\n\nRequirements:\n${data.requirements}` : "",
      ].join("");
      setF((cur) => ({ ...cur, description: body.trim() }));
      toast.success("JD drafted — review and edit before publishing.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI drafter unavailable, try again");
    } finally {
      setDrafting(false);
    }
  };

  const load = async () => {
    const [jr, ir] = await Promise.all([
      api.get("/jobs"),
      api.get("/industries", { params: { active: true } }),
    ]);
    setJobs(jr.data);
    setIndustries(ir.data.map(i => i.name));
    if (isAdmin) {
      try {
        const cr = await api.get("/clients");
        setClientMap(Object.fromEntries(cr.data.map(c => [c.client_id, c.name])));
      } catch { /* non-fatal */ }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openCreate = () => { setEditingId(null); setF(EMPTY); setOpen(true); };
  const openEdit = (j) => {
    setEditingId(j.job_id);
    setF({ ...j, skills: (j.skills || []).join(", ") });
    setOpen(true);
  };
  const save = async () => {
    if (!f.title || !f.location || !f.description) {
      toast.error("Title, location and description are required");
      return;
    }
    try {
      const payload = { ...f, skills: f.skills.split(",").map(s => s.trim()).filter(Boolean) };
      delete payload.job_id;
      delete payload.posted_at;
      delete payload.is_active;
      delete payload.status;
      delete payload.client_id;
      delete payload.application_count;
      delete payload.posted_by_role;
      delete payload.posted_by_user_id;
      if (editingId) {
        await api.put(`/jobs/${editingId}`, payload);
        toast.success(isClient ? "Job updated — re-submitted for admin approval" : "Job updated");
      } else {
        await api.post("/jobs", payload);
        toast.success(isClient ? "Job submitted for admin approval" : "Job posted");
      }
      setOpen(false); setF(EMPTY); setEditingId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    try { await api.delete(`/jobs/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };
  const moderate = async (id, status) => {
    try {
      await api.patch(`/jobs/${id}/status`, null, { params: { status } });
      toast.success(status === "approved" ? "Job approved & published" : "Job rejected");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const pendingCount = jobs.filter(j => j.status === "pending").length;
  const visible = pendingOnly ? jobs.filter(j => j.status === "pending") : jobs;
  const { page, setPage, totalPages, pageItems, rangeStart, rangeEnd, total } = usePagination(visible, 20);

  return (
    <div className="space-y-8" data-testid="jobs-admin-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow text-navy/50">{isClient ? "My postings" : "Admin"}</div>
          <h1 className="serif-display text-4xl mt-2">Jobs</h1>
          {isAdmin && pendingCount > 0 && (
            <div className="mt-2 text-sm text-amber-600" data-testid="jobs-pending-count">
              {pendingCount} pending {pendingCount === 1 ? "review" : "reviews"}
            </div>
          )}
          {isClient && (
            <p className="mt-2 text-sm text-navy/60 max-w-lg">Post an opening for your company. New postings go to Ravemora admins for review before appearing on the public careers page.</p>
          )}
        </div>
        {(isAdmin || isClient) && (
          <button className="btn-primary inline-flex items-center gap-2" onClick={openCreate} data-testid="new-job-btn"><Plus size={14}/> {isClient ? "Post a job" : "New job"}</button>
        )}
      </div>

      <div className="bg-white border border-navy/10">
        {isAdmin && (
          <div className="border-b border-navy/10 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setPendingOnly(!pendingOnly)}
                className={`px-3 py-1.5 border label-eyebrow inline-flex items-center gap-2 transition-colors ${pendingOnly ? "bg-navy text-gold border-navy" : "bg-transparent text-navy border-navy/30 hover:border-navy"}`}
                data-testid="pending-only-toggle"
              >
                <span>Pending only</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${pendingOnly ? "bg-gold/20 text-gold" : "bg-amber-100 text-amber-700"}`}>{pendingCount}</span>
              </button>
            </div>
            <div className="text-xs text-navy/50">Showing {visible.length} of {jobs.length}</div>
          </div>
        )}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Industry</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Experience</TableHead>
              {isAdmin && <TableHead>Posted by</TableHead>}
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageItems.map((j) => (
              <TableRow key={j.job_id} data-testid={`job-row-${j.job_id}`}>
                <TableCell className="serif-display text-[15px] font-medium">{j.title}</TableCell>
                <TableCell><Badge variant="outline" className="border-gold text-gold">{j.industry}</Badge></TableCell>
                <TableCell>{j.location}</TableCell>
                <TableCell>{j.experience}</TableCell>
                {isAdmin && (
                  <TableCell data-testid={`job-poster-${j.job_id}`}>
                    {j.posted_by_role === "client" ? (
                      <div className="flex flex-col">
                        <span className="serif-display text-navy">{clientMap[j.client_id] || j.client_id || "—"}</span>
                        <span className="text-[10px] uppercase tracking-wider text-gold">Client submission</span>
                      </div>
                    ) : (
                      <span className="text-navy/60 text-sm">Admin</span>
                    )}
                  </TableCell>
                )}
                <TableCell>
                  <Badge variant="outline" className={STATUS_STYLE[j.status] || "border-navy/30 text-navy/70"} data-testid={`job-status-${j.job_id}`}>
                    {(j.status || "approved").toUpperCase()}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-2">
                    {isAdmin && j.status === "pending" && (
                      <>
                        <button title="Approve" onClick={() => moderate(j.job_id, "approved")} className="p-1 border border-emerald-500 text-emerald-600 hover:bg-emerald-50" data-testid={`approve-job-${j.job_id}`}><Check size={14}/></button>
                        <button title="Reject" onClick={() => moderate(j.job_id, "rejected")} className="p-1 border border-rose-500 text-rose-600 hover:bg-rose-50" data-testid={`reject-job-${j.job_id}`}><X size={14}/></button>
                      </>
                    )}
                    {isAdmin && j.status === "rejected" && (
                      <button title="Re-approve" onClick={() => moderate(j.job_id, "approved")} className="p-1 border border-emerald-500 text-emerald-600 hover:bg-emerald-50" data-testid={`reapprove-job-${j.job_id}`}><Check size={14}/></button>
                    )}
                    <RowActions
                      onEdit={() => openEdit(j)}
                      onDelete={() => del(j.job_id)}
                      editTestid={`edit-job-${j.job_id}`}
                      deleteTestid={`delete-job-${j.job_id}`}
                      itemLabel="this job"
                    />
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={isAdmin ? 7 : 6} className="text-center text-navy/50 py-10">
                  {pendingOnly ? "No pending job submissions right now — you're all caught up." : (isClient ? "You have not posted any jobs yet. Click ‘Post a job’ to add your first opening." : "No jobs yet.")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination page={page} totalPages={totalPages} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} onPrev={() => setPage((p) => Math.max(1, p - 1))} onNext={() => setPage((p) => Math.min(totalPages, p + 1))} testidPrefix="jobs-table" />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="serif-display text-2xl">{editingId ? "Edit job" : (isClient ? "Post a job" : "Create job")}</DialogTitle>
            <DialogDescription>
              {isClient
                ? "Fill in the role details. Ravemora admins will review your posting and publish it on the public careers page once approved."
                : "Fill in the role details. Admin-posted jobs are published immediately."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label>Title</Label><Input value={f.title} onChange={e=>setF({...f,title:e.target.value})} data-testid="job-title"/></div>
            <div><Label>Location</Label><Input value={f.location} onChange={e=>setF({...f,location:e.target.value})} data-testid="job-location"/></div>
            <div><Label>Department</Label><Input value={f.department} onChange={e=>setF({...f,department:e.target.value})} data-testid="job-department"/></div>
            <div>
              <Label>Industry</Label>
              <Select value={f.industry} onValueChange={(v) => setF({ ...f, industry: v })}>
                <SelectTrigger data-testid="job-industry"><SelectValue placeholder="Select an industry" /></SelectTrigger>
                <SelectContent>
                  {industries.map((n) => (
                    <SelectItem key={n} value={n}>{n}</SelectItem>
                  ))}
                  {/* Preserve legacy value if editing an old row */}
                  {f.industry && !industries.includes(f.industry) && (
                    <SelectItem value={f.industry}>{f.industry} (legacy)</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Experience</Label><Input value={f.experience} onChange={e=>setF({...f,experience:e.target.value})} data-testid="job-experience"/></div>
            <div><Label>Skills (comma separated)</Label><Input value={f.skills} onChange={e=>setF({...f,skills:e.target.value})} data-testid="job-skills"/></div>
            <div className="md:col-span-2">
              <div className="flex items-center justify-between">
                <Label>Description</Label>
                <button
                  type="button"
                  onClick={draftWithAI}
                  disabled={drafting}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-navy hover:text-gold px-2 py-1 border border-navy/20 hover:border-gold transition-colors disabled:opacity-50"
                  data-testid="job-draft-ai"
                >
                  {drafting ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
                  {drafting ? "Drafting…" : "Draft with AI"}
                </button>
              </div>
              <Textarea rows={8} value={f.description} onChange={e=>setF({...f,description:e.target.value})} data-testid="job-description"/>
            </div>
          </div>
          <DialogFooter>
            <button className="btn-outline" onClick={()=>setOpen(false)}>Cancel</button>
            <button className="btn-primary" onClick={save} data-testid="job-save-submit">
              {editingId ? (isClient ? "Save & resubmit for approval" : "Save changes") : (isClient ? "Submit for approval" : "Publish job")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
